'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from '@/components/antd-wrapper/modal/global/modal';
import { Button as AntdButton } from 'antd';
import ConfirmModal from '@/components/deep-mining/confirm-modal';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getMemoryDetail,
  getMemoryAgents,
  getMemoryTypeOptions,
  createMemory,
  updateMemory as updateMemoryApi,
} from '@/services/memory';
import { useI18n } from '@/hooks/use-i18n';

type IMemoryItem = MISC.MemoryItem;
type IMemoryAgentItem = MISC.MemoryAgentItem;
type MemoryExperienceType = MISC.MemoryExperienceType;

export const OPEN_MEMORY_LIBRARY_EVENT = 'openMemoryLibrary';
export const MEMORY_SAVED_EVENT = 'memorySaved';

export function triggerMemoryLibrary(
  mode: 'add' | 'edit' = 'add',
  id?: string,
) {
  const event = new CustomEvent(OPEN_MEMORY_LIBRARY_EVENT, {
    detail: { mode, id },
  });
  window.dispatchEvent(event);
}

interface FormState {
  title: string;
  content: string;
  agent_key: string;
  memory_type: MemoryExperienceType | '';
}

interface FormErrors {
  title: string | null;
  content: string | null;
  agent_key: string | null;
  memory_type: string | null;
}

export function MemoryLibrary() {
  const { t } = useI18n(['components', 'common']);

  const [isOpen, setIsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [mode, setMode] = useState<'add' | 'edit'>('add');
  const [editingId, setEditingId] = useState<string | null>(null);
  const isDirtyRef = useRef(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [agents, setAgents] = useState<IMemoryAgentItem[]>([]);
  const [experienceTypeOptions, setExperienceTypeOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const [form, setForm] = useState<FormState>({
    title: '',
    content: '',
    agent_key: '',
    memory_type: '',
  });

  const [errors, setErrors] = useState<FormErrors>({
    title: null,
    content: null,
    agent_key: null,
    memory_type: null,
  });

  const resetForm = () => {
    setForm({ title: '', content: '', agent_key: '', memory_type: '' });
    setErrors({
      title: null,
      content: null,
      agent_key: null,
      memory_type: null,
    });
    setEditingId(null);
    setSaveLoading(false);
    setDetailLoading(false);
    isDirtyRef.current = false;
  };

  const fetchAgents = useCallback(async (mode?: 'add' | 'edit') => {
    try {
      const list = await getMemoryAgents();
      setAgents(list ?? []);
      if (mode === 'add' && list?.length) {
        setForm((prev) => ({ ...prev, agent_key: list[0].agent_key }));
      }
    } catch {
      setAgents([]);
    }
  }, []);

  const fetchExperienceTypeOptions = useCallback(async () => {
    try {
      const options = await getMemoryTypeOptions();
      setExperienceTypeOptions(options ?? []);
    } catch {
      // 静默失败，保持空数组
    }
  }, []);

  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent<{ mode: 'add' | 'edit'; id?: string }>)
        .detail;
      const nextMode = detail?.mode ?? 'add';
      const id = detail?.id ?? null;

      resetForm();
      setMode(nextMode);
      fetchAgents(nextMode);
      fetchExperienceTypeOptions();

      if (nextMode === 'edit' && id) {
        setEditingId(id);
        setIsOpen(true);
        setDetailLoading(true);
        try {
          const item = await getMemoryDetail(id);
          setForm({
            title: item.title,
            content: item.content,
            agent_key: item.agent_key ?? '',
            memory_type: item.memory_type ?? '',
          });
        } catch {
          toast.error(t('components:memory.saveFailed'));
          setIsOpen(false);
        } finally {
          setDetailLoading(false);
        }
      } else {
        setIsOpen(true);
      }
    };

    window.addEventListener(OPEN_MEMORY_LIBRARY_EVENT, handler);
    return () => window.removeEventListener(OPEN_MEMORY_LIBRARY_EVENT, handler);
  }, [t, fetchAgents, fetchExperienceTypeOptions]);

  const validateField = (
    name: keyof FormState,
    value: string,
  ): string | null => {
    switch (name) {
      case 'title':
        return value.trim() ? null : t('components:memory.nameRequired');
      case 'content':
        if (!value.trim()) return t('components:memory.contentRequired');
        if (value.trim().length > 10000)
          return t('components:memory.contentMaxLength');
        return null;
      case 'agent_key':
        return value ? null : t('components:memory.agentRequired');
      case 'memory_type':
        return value ? null : t('components:memory.experienceTypeRequired');
      default:
        return null;
    }
  };

  const setField = (name: keyof FormState, value: string) => {
    isDirtyRef.current = true;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateAll = (): boolean => {
    const next: FormErrors = {
      title: validateField('title', form.title),
      content: validateField('content', form.content),
      agent_key: validateField('agent_key', form.agent_key),
      memory_type: validateField('memory_type', form.memory_type),
    };
    setErrors(next);
    return Object.values(next).every((v) => v === null);
  };

  const handleSave = async () => {
    if (!validateAll()) return;
    setSaveLoading(true);
    try {
      let saved: IMemoryItem;
      if (mode === 'add') {
        saved = await createMemory({
          title: form.title,
          content: form.content,
          agent_key: form.agent_key || 'common',
          memory_type: (form.memory_type as MemoryExperienceType) || undefined,
        });
      } else {
        saved = await updateMemoryApi(editingId!, {
          title: form.title,
          content: form.content,
          agent_key: form.agent_key || 'common',
          memory_type: (form.memory_type as MemoryExperienceType) || undefined,
        });
      }
      toast.success(t('components:memory.saveSuccess'));
      window.dispatchEvent(
        new CustomEvent(MEMORY_SAVED_EVENT, { detail: { mode, item: saved } }),
      );
      setIsOpen(false);
      resetForm();
    } catch {
      // toast.error(t('components:memory.saveFailed'));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClose = () => {
    if (isDirtyRef.current) {
      setShowConfirm(true);
    } else {
      setIsOpen(false);
      resetForm();
    }
  };

  const handleConfirmDiscard = () => {
    setShowConfirm(false);
    setIsOpen(false);
    resetForm();
  };

  const title =
    mode === 'add'
      ? t('components:memory.newMemory')
      : t('components:memory.editMemory');

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={handleClose}
        title=""
        width={800}
        height={633}
        headerHeight={0}
        footer={null}
        maskClosable={false}
        classNames={{ content: '!pb-[0]' }}
        className="memory-library-modal"
      >
        <div className="flex flex-col h-full overflow-hidden px-6 pb-6">
          {/* Header */}
          <div className="text-[18px] shrink-0 h-14.5 flex items-center justify-center w-full font-semibold text-[#000000BF] relative">
            <div className="text-center">{title}</div>
          </div>

          {/* Form */}
          <div className="flex-1 space-y-3 overflow-y-auto pb-1">
            {detailLoading ? (
              <div className="flex items-center justify-center h-40 text-[13px] text-[rgba(0,0,0,0.25)]">
                {t('components:memorySpace.loading')}
              </div>
            ) : (
              <>
                {/* 名称 */}
                <div>
                  <div className="flex items-center mb-2 text-[14px] font-semibold">
                    <span className="text-[#eb5149] mr-1">*</span>
                    <span className="text-[#404040]">
                      {t('common:nameLabel')}
                    </span>
                  </div>
                  <Input
                    disabled={saveLoading}
                    value={form.title}
                    onChange={(e) => setField('title', e.target.value)}
                    onBlur={() =>
                      setErrors((prev) => ({
                        ...prev,
                        title: validateField('title', form.title),
                      }))
                    }
                    placeholder={t('components:memory.namePlaceholder')}
                    className="w-full px-3 bg-[#F8F8FA] text-[16px]! rounded-lg border-none outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:border-none h-11"
                  />
                  {errors.title && (
                    <p className="text-[14px] text-[#eb5149] mt-1">
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* 所属 Agent */}
                <div>
                  <div className="flex items-center mb-2 text-[14px] font-semibold">
                    <span className="text-[#eb5149] mr-1">*</span>
                    <span className="text-[#404040]">
                      {t('components:memory.agentLabel')}
                    </span>
                  </div>
                  <Select
                    value={form.agent_key}
                    onValueChange={(v) => setField('agent_key', v)}
                    disabled={saveLoading}
                  >
                    <SelectTrigger
                      className="w-full bg-[#F8F8FA] border-none shadow-none focus:ring-0 focus-visible:ring-0 text-[16px] h-11! px-3 rounded-[8px]"
                      onBlur={() =>
                        setErrors((prev) => ({
                          ...prev,
                          agent_key: validateField('agent_key', form.agent_key),
                        }))
                      }
                    >
                      <SelectValue
                        placeholder={t('components:memory.agentPlaceholder')}
                      />
                    </SelectTrigger>
                    <SelectContent className="z-1100">
                      {agents.map((a) => (
                        <SelectItem key={a.agent_key} value={a.agent_key}>
                          {a.agent_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.agent_key && (
                    <p className="text-[14px] text-[#eb5149] mt-1">
                      {errors.agent_key}
                    </p>
                  )}
                </div>

                {/* 经验类型 */}
                <div>
                  <div className="flex items-center mb-2 text-[14px] font-semibold">
                    <span className="text-[#eb5149] mr-1">*</span>
                    <span className="text-[#404040]">
                      {t('components:memory.experienceTypeLabel')}
                    </span>
                  </div>
                  <Select
                    value={form.memory_type}
                    onValueChange={(v) => setField('memory_type', v)}
                    disabled={saveLoading}
                  >
                    <SelectTrigger
                      className="w-full bg-[#F8F8FA] border-none shadow-none focus:ring-0 focus-visible:ring-0 text-[16px] h-11! px-3 rounded-[8px]"
                      onBlur={() =>
                        setErrors((prev) => ({
                          ...prev,
                          memory_type: validateField(
                            'memory_type',
                            form.memory_type,
                          ),
                        }))
                      }
                    >
                      <SelectValue
                        placeholder={t(
                          'components:memory.experienceTypePlaceholder',
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent className="z-1100">
                      {experienceTypeOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.memory_type && (
                    <p className="text-[14px] text-[#eb5149] mt-1">
                      {errors.memory_type}
                    </p>
                  )}
                </div>

                {/* 内容 */}
                <div>
                  <div className="flex items-center mb-2 text-[14px] font-semibold">
                    <span className="text-[#eb5149] mr-1">*</span>
                    <span className="text-[#404040]">
                      {t('common:contentLabel')}
                    </span>
                  </div>
                  <textarea
                    disabled={saveLoading}
                    value={form.content}
                    onChange={(e) => setField('content', e.target.value)}
                    onBlur={() =>
                      setErrors((prev) => ({
                        ...prev,
                        content: validateField('content', form.content),
                      }))
                    }
                    placeholder={t('components:memory.contentPlaceholder')}
                    className="w-full h-50 p-3 rounded-lg resize-none bg-[#F8F8FA] text-[16px] focus-visible:outline-none focus-visible:ring-0 focus-visible:border-none disabled:opacity-50"
                  />
                  {errors.content && (
                    <p className="text-[14px] text-[#eb5149] mt-1">
                      {errors.content}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer buttons */}
          <div className="flex justify-end items-center gap-4 shrink-0 pt-2">
            <AntdButton
              type="text"
              onClick={handleClose}
              className="rounded-md px-7 py-3 bg-[#F1F1FC] hover:bg-[#f1f1fc] text-[#7171EE] text-[13px] h-auto"
            >
              {t('common:cancel')}
            </AntdButton>
            <AntdButton
              disabled={saveLoading || detailLoading}
              onClick={handleSave}
              loading={saveLoading}
              type="primary"
              className="bg-[#7171ee] hover:bg-[#5959c7] text-white rounded-md px-7 py-3 text-[13px] h-auto"
            >
              {saveLoading ? t('common:saving') : t('common:save')}
            </AntdButton>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showConfirm}
        content={t('components:memory.discardConfirm')}
        showCancel
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmDiscard}
        zIndex={1200}
      />
    </>
  );
}
